import type { Asset, AssetType } from "schemas/generated/asset";

import * as React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Input } from "@replo/design-system/components/input/Input";
import { Separator } from "@replo/design-system/components/separator/Separator";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@replo/design-system/components/tabs/Tabs";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { ReploAssetsGrid } from "@v2/features/assets/components/asset-grid/ReploAssetsGrid";
import { ShopifyAssetsGrid } from "@v2/features/assets/components/asset-grid/ShopifyAssetsGrid";
import { ACCEPT_FILE_EXTENSIONS } from "@v2/features/assets/constants";
import { useReploFileDropZone } from "@v2/features/assets/useFileDropZone";
import { getAssetTypeFromFile } from "@v2/features/assets/useUploadAsset";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { assetUtils } from "@v2/trpc/routes/asset";
import { Search, Upload } from "lucide-react";
import { useDebouncedState } from "replo-utils/react/use-debounced-state";
import { isShopifyIntegrationEnabled } from "schemas/utils";

type ActiveTab = "reploAssets" | "shopifyAssets";

const ASSETS_TABS: { label: string; value: ActiveTab }[] = [
  {
    label: "Replo",
    value: "reploAssets",
  },
  {
    label: "Shopify",
    value: "shopifyAssets",
  },
];

export type AssetsLibraryRef = {
  uploadFiles: (files: File[]) => void;
};

type AssetsLibraryProps = {
  assetTypes?: ("image" | "video")[];
  onAssetSelected?: (asset: Asset) => void;
  shouldAllowActions?: boolean;
  // NOTE (Kevin, 2025-12-15): When true, disables the local dropzone drag-and-drop functionality.
  // Use this when a parent component (e.g., AssetModifier, ContextItemsMenu) has registered
  // a global file drop interceptor via useChatFileDropZone to handle file drops at the
  // document level. This prevents duplicate uploads that would occur if both the local
  // dropzone and the global interceptor processed the same drop event.
  // The upload button still works when this is true - only drag-and-drop is disabled.
  isDropDisabled?: boolean;
};

const AssetsLibrary = React.forwardRef<AssetsLibraryRef, AssetsLibraryProps>(
  function AssetsLibrary(
    {
      assetTypes = ["image", "video"],
      onAssetSelected,
      shouldAllowActions = true,
      isDropDisabled = false,
    },
    ref,
  ) {
    const { project } = useCurrentProjectContext();
    const hasShopifyIntegration = isShopifyIntegrationEnabled(project);
    const projectId = useCurrentProjectId();
    const [activeTab, setActiveTab] = React.useState<ActiveTab>("reploAssets");
    const [searchTerm, debouncedSearchTerm, setSearchTerm] =
      useDebouncedState("");
    const {
      getRootProps,
      getInputProps,
      openFileDialog,
      droppedFilesMap,
      isDragActive,
      uploadFiles,
    } = useReploFileDropZone({
      onUploadComplete: ({ url, file, id }) => {
        void assetUtils.list.invalidate();
        if (onAssetSelected && file && id) {
          const assetType = getAssetTypeFromFile(file);
          const fileExtension = file.name.split(".").at(-1) ?? "";
          const asset: Asset = {
            id,
            name: file.name,
            createdAt: new Date(),
            updatedAt: new Date(),
            url,
            type: assetType as AssetType,
            sizeBytes: file.size,
            fileExtension,
            width: null,
            height: null,
            projectId: projectId ?? null,
            altText: null,
            embeddingText: null,
          };
          onAssetSelected(asset);
        }
      },
      acceptDropAssetType: ACCEPT_FILE_EXTENSIONS,
      allowClickToUpload: true,
      isDisabled: activeTab === "shopifyAssets" || isDropDisabled,
    });

    React.useImperativeHandle(ref, () => ({
      uploadFiles,
    }));

    return (
      <div className="flex min-h-0 flex-1 flex-col">
        {hasShopifyIntegration && (
          <div className="px-3">
            <Tabs
              value={activeTab}
              onValueChange={(value) => setActiveTab(value as ActiveTab)}
              defaultValue={activeTab}
            >
              <TabsList size="sm" variant="outline">
                {ASSETS_TABS.map((tab) => (
                  <TabsTrigger key={tab.value} value={tab.value} size="sm">
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
        )}
        <div className="flex min-h-0 flex-1 flex-col gap-2 pt-1.5 px-3 pb-2 overflow-hidden">
          <div className="flex gap-2">
            <Input
              autoComplete="off"
              placeholder="Search"
              className="grow"
              size="sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              startEnhancer={<Search />}
            />
            {activeTab === "reploAssets" && (
              <Button
                icon
                variant="secondary"
                onClick={openFileDialog}
                size="sm"
                aria-label="Upload"
              >
                <Upload />
              </Button>
            )}
          </div>

          <Separator />

          {activeTab === "reploAssets" ? (
            <ReploAssetsGrid
              searchTerm={debouncedSearchTerm}
              getDropzoneInputProps={getInputProps}
              getDropzoneRootProps={getRootProps}
              assetTypes={assetTypes}
              onAssetSelected={onAssetSelected}
              shouldAllowActions={shouldAllowActions}
              droppedFilesMap={droppedFilesMap}
              isDragActive={isDragActive}
            />
          ) : (
            <ShopifyAssetsGrid
              searchTerm={debouncedSearchTerm}
              assetTypes={assetTypes}
              onAssetSelected={onAssetSelected}
            />
          )}
        </div>
      </div>
    );
  },
);

export default AssetsLibrary;
